/**
 * Card Utility Functions
 * Shared helpers for card data manipulation and display
 */

/**
 * Normalize card ID for lookup: uppercase set code, preserve suffix
 * Examples: ogn-001 -> OGN-001, OGN-007a -> OGN-007a
 */
export function normalizeCardId(id) {
  if (!id || typeof id !== 'string') {
    return (id || '').trim().toUpperCase();
  }
  const parts = id.trim().split('-');
  if (parts.length < 2) {
    return id.trim().toUpperCase();
  }
  parts[0] = parts[0].toUpperCase();
  return parts.join('-');
}

/**
 * Check if card is a battlefield
 */
export function isBattlefieldCard(card) {
  if (!card) return false;
  const type = card.cardType?.type?.[0]?.id || card.cardType?.type?.[0]?.label || '';
  return /battlefield/i.test(type);
}

/**
 * Get display name for card
 * Handles special case: Starter cards show "Champion, Title" (cardmarketName) instead of "Title - Starter"
 */
export function getCardDisplayName(card) {
  if (!card) return '';
  if (card.name?.endsWith(' - Starter') && card.cardmarketName) {
    return card.cardmarketName;
  }
  return card.name ?? '';
}

/**
 * Group cards by ID (exact same card ID)
 * Returns array of { card, count } sorted by ID
 * Base vs showcase variants have different IDs, so they're grouped separately
 */
export function groupCardsById(cards) {
  const byId = new Map();

  for (const c of cards) {
    const id = c?.id;
    if (!id) continue;

    const existing = byId.get(id);
    if (existing) {
      existing.count += 1;
    } else {
      byId.set(id, { card: c, count: 1 });
    }
  }

  return [...byId.values()].sort((a, b) =>
    (a.card?.id ?? '').localeCompare(b.card?.id ?? '', undefined, { numeric: true })
  );
}

/**
 * Strip variant suffix for API lookup
 * Example: OGN-036-1 -> OGN-036
 */
export function stripVariant(id) {
  return String(id || '').split('-').slice(0, 2).join('-');
}

/**
 * Get card type label
 */
export function getCardType(card) {
  if (!card) return '';
  return card.cardType?.type?.[0]?.label || card.cardType?.type?.[0]?.id || '';
}

/**
 * Get card rarity label
 */
export function getCardRarity(card) {
  if (!card) return '';
  return card.rarity?.value?.label || card.rarity || '';
}

/**
 * Get illustrator/artist name (handles values[0].label or .name)
 */
export function getIllustratorName(card) {
  if (!card) return 'Unknown';
  const v = card.illustrator?.values?.[0];
  return v?.label ?? v?.name ?? card.illustrator?.name ?? 'Unknown';
}

/**
 * Format set + number for display (e.g. "OGN • 299/298" from publicCode or id)
 */
export function getCardSetDisplay(card) {
  if (!card) return '';
  const code = card.publicCode || card.id || '';
  const parts = code.split('-');
  if (parts.length >= 2) {
    const setCode = (parts[0] || '').toUpperCase();
    const rest = parts.slice(1).join('/');
    return rest ? `${setCode} • ${rest}` : setCode;
  }
  return code;
}

/**
 * Get card domain labels
 */
export function getCardDomains(card) {
  if (!card) return [];
  const domains = card.domain?.values || [];
  return domains.map(d => d.label || d.id || String(d));
}

const DOMAIN_IDS = ['body', 'calm', 'chaos', 'fury', 'mind', 'order'];

/**
 * Get domain values for display (DomainBadges). For runes with no domain data, derive from name (e.g. "Body Rune").
 */
export function getCardDomainsForDisplay(card) {
  if (!card) return [];
  const values = card.domain?.values || [];
  if (values.length) return values;
  const type = (card.cardType?.type?.[0]?.id || card.cardType?.type?.[0]?.label || '').toLowerCase();
  const name = (card.name || '').toLowerCase();
  if (!/rune/.test(type)) return [];
  for (const id of DOMAIN_IDS) {
    if (name.includes(id + ' rune')) {
      const label = id.charAt(0).toUpperCase() + id.slice(1);
      return [{ id, label }];
    }
  }
  return [];
}

/**
 * Get card energy cost
 */
export function getCardEnergy(card) {
  if (!card) return null;
  return card.energy?.value?.id ?? card.energy ?? null;
}

/**
 * Get card power
 */
export function getCardPower(card) {
  if (!card) return null;
  return card.power?.value?.id ?? card.power ?? null;
}

/**
 * Get card art image URL (cardImage.url or image_url).
 * Uses only this card's image — never substitute base/variant art (each id, e.g. OGN-197 vs OGN-197b, has its own art).
 * Resolves relative /api/card-art/... against VITE_API_URL when set (e.g. production).
 */
export function getCardImageUrl(card) {
  if (!card) return null;
  const raw = card.cardImage?.url ?? card.image_url ?? null;
  if (!raw || typeof raw !== 'string') return null;
  const base = import.meta.env.VITE_API_URL;
  if (base && raw.startsWith('/')) {
    return `${base.replace(/\/$/, '')}${raw}`;
  }
  return raw;
}

/**
 * Get raw card text (rules/ability) — may be HTML
 */
export function getCardTextRaw(card) {
  if (!card) return '';
  return card.text?.richText?.body ?? card.text?.body ?? card.body ?? '';
}

/**
 * Riftbound inline tokens :rb_name:: or :rb_name: → [Name] for display
 */
const RB_TOKEN_LABELS = {
  exhaust: 'Exhaust',
  energy: 'Energy',
  might: 'Might',
  power: 'Power',
  rune_rainbow: 'Rune',
  calm: 'Calm',
  fury: 'Fury',
  body: 'Body',
  mind: 'Mind',
  order: 'Order',
  chaos: 'Chaos',
  fast: 'Fast',
  slow: 'Slow',
  conquer: 'Conquer',
  recall: 'Recall',
  strike: 'Strike',
  block: 'Block',
  challenge: 'Challenge'
};

/** energy_0 .. energy_8 → "0" .. "8" for fallback label */
for (let i = 0; i <= 8; i++) RB_TOKEN_LABELS[`energy_${i}`] = String(i);

function replaceRiftboundTokens(text) {
  return text.replace(/:rb_(\w+)::?/gi, (_, name) => {
    const key = name.toLowerCase();
    const label = RB_TOKEN_LABELS[key] || name.charAt(0).toUpperCase() + name.slice(1).toLowerCase();
    return `[${label}]`;
  });
}

/**
 * Strip HTML and decode entities (shared for getCardText and getCardTextSegments)
 */
function stripHtmlToText(raw) {
  if (!raw) return '';
  return raw
    .replace(/<br\s*\/?>/gi, '\n')
    .replace(/<\/p>\s*<p>/gi, '\n\n')
    .replace(/<[^>]+>/g, '')
    .replace(/&nbsp;/g, ' ')
    .replace(/&amp;/g, '&')
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .trim();
}

/**
 * Get card text as plain text (strip HTML, resolve :rb_*: tokens to [Label])
 */
export function getCardText(card) {
  const raw = getCardTextRaw(card);
  const stripped = stripHtmlToText(raw);
  return replaceRiftboundTokens(stripped);
}

const RB_TOKEN_REGEX = /:rb_(\w+)::?/gi;

/**
 * Get card text as segments for rendering with inline icons.
 * Returns array of { type: 'text', value: string } | { type: 'icon', name: string, label: string }.
 */
export function getCardTextSegments(card) {
  const raw = getCardTextRaw(card);
  const stripped = stripHtmlToText(raw);
  if (!stripped) return [];

  const segments = [];
  let lastIndex = 0;
  let m;
  RB_TOKEN_REGEX.lastIndex = 0;
  while ((m = RB_TOKEN_REGEX.exec(stripped)) !== null) {
    if (m.index > lastIndex) {
      segments.push({ type: 'text', value: stripped.slice(lastIndex, m.index) });
    }
    const name = m[1].toLowerCase();
    const label = RB_TOKEN_LABELS[name] || name.charAt(0).toUpperCase() + name.slice(1);
    segments.push({ type: 'icon', name, label });
    lastIndex = m.index + m[0].length;
  }
  if (lastIndex < stripped.length) {
    segments.push({ type: 'text', value: stripped.slice(lastIndex) });
  }
  return segments;
}
